// src/services/pftReviewTargets.js
//
// Which box a PFT Uploads review item is about, so the card can highlight it (decided
// 2026-10-12: "highlight the box it's referring to"). Firebase-free and tested.
//
// A review item's `field` is usually the box itself (a test key like "fev1fvcPre", or a
// patient detail like "dob"). Automatic checks are "check1", "check2", ... and name their
// box only in the reason text the extraction job writes (pft_quick_test.py build_rows /
// use_printed_values), so those are read from the wording. A check that names no box
// (e.g. "Post values present but post_bd_performed is false") highlights nothing.

import { PFT_TEST_VALUE_KEYS } from "./pftTests";

export const PATIENT_TARGET_KEYS = ["name", "dob", "mrn", "sex", "phone", "address"];

// Checks about one Pre or Post phase: "<Pre|Post> <rest>". First match wins, so
// "FEV1/FVC" is tried before "FEV1".
const PHASE_CHECKS = [
  [/^FEV1\/FVC\b/, ["fev1fvc"]],
  [/^FEV1 > FVC/, ["fev1Actual", "fvcActual"]],
  [/^FEV1 (?:unit|\(L\))/, ["fev1Actual"]],
  [/^FVC (?:unit|\(L\))/, ["fvcActual"]],
  [/^FEV1 (?:printed )?%pred/, ["fev1"]],
  [/^FVC (?:printed )?%pred/, ["fvc"]],
];
// Checks about the whole report.
const REPORT_CHECKS = [
  [/^FEV1 reversibility \(mL\)/, ["revMl"]],
  [/^printed %change/, ["revPercent"]],
  [/^RV and TLC units differ/, ["rvUnit", "tlcUnit"]],
  [/^RV\/TLC\b/, ["rvTlc"]],
  [/^TLC measured/, ["tlc"]],
  [/^RV measured/, ["rv"]],
  [/^DLCO measured/, ["dlco"]],
  [/^a reference value may have been copied/, ["tlc", "rv", "dlco"]],
];

const testKeys = new Set(PFT_TEST_VALUE_KEYS);

const fromCheck = (reason) => {
  const msg = String(reason || "").replace(/^automatic check:\s*/i, "").trim();
  const phase = /^(Pre|Post) (.*)$/.exec(msg);
  if (phase) {
    const hit = PHASE_CHECKS.find(([re]) => re.test(phase[2]));
    return hit ? hit[1].map((k) => `${k}${phase[1]}`) : [];
  }
  const hit = REPORT_CHECKS.find(([re]) => re.test(msg));
  return hit ? hit[1] : [];
};

/**
 * The boxes one review item is about: { test: [test keys], patient: [patient keys] }.
 * Keys that don't exist on the page are dropped.
 */
export const reviewTargets = (item) => {
  const field = (item && item.field) || "";
  if (field === "identity") return { test: [], patient: ["name", "dob"] };
  if (PATIENT_TARGET_KEYS.includes(field)) return { test: [], patient: [field] };
  if (testKeys.has(field)) return { test: [field], patient: [] };
  if (/^check\d+$/.test(field)) return { test: fromCheck(item.reason).filter((k) => testKeys.has(k)), patient: [] };
  return { test: [], patient: [] };
};

/**
 * For one test on a card: Map(test key -> review item) of the UNTICKED review items about it.
 * A test folded from several PDFs (alsoIn) takes the items of each of its imports.
 */
export const reviewBoxesForTest = (test, items) => {
  const ids = new Set([test && test.importId, ...((test && test.alsoIn) || []).map((a) => a.importId)].filter(Boolean));
  const out = new Map();
  for (const r of items || []) {
    if (r.resolved || !ids.has(r.importId)) continue;
    for (const k of reviewTargets(r).test) if (!out.has(k)) out.set(k, r);
  }
  return out;
};

/** Map(patient key -> review item) of the unticked review items about the patient's details. */
export const reviewBoxesForPatient = (items) => {
  const out = new Map();
  for (const r of items || []) {
    if (r.resolved) continue;
    for (const k of reviewTargets(r).patient) if (!out.has(k)) out.set(k, r);
  }
  return out;
};
